import { ShieldAlert } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { Permission } from "@/lib/api/roles";

export const DATA_ACCESS_RESOURCES = ["mentee_pii", "mentee_phi"] as const;
type DataAccessResource = (typeof DATA_ACCESS_RESOURCES)[number];

const COPY: Record<DataAccessResource, { title: string; description: string }> = {
  mentee_pii: {
    title: "Personally Identifying Information (PII)",
    description:
      "Name, email, phone, profile photo, and other fields that directly identify a teen.",
  },
  mentee_phi: {
    title: "Protected Health Information (PHI)",
    description:
      "Birthday, location, guardian and emergency contact details, clinical notes, bio, and behavioral data.",
  },
};

interface DataAccessSectionProps {
  permissions: readonly Permission[];
  selectedIds: Set<string>;
  onToggle: (permissionId: string) => void;
  disabled?: boolean;
}

export function DataAccessSection({
  permissions,
  selectedIds,
  onToggle,
  disabled,
}: DataAccessSectionProps) {
  const dataAccessPerms = permissions.filter(
    (p) =>
      DATA_ACCESS_RESOURCES.includes(p.resource as DataAccessResource) &&
      p.action === "read",
  );

  if (dataAccessPerms.length === 0) return null;

  return (
    <section className="rounded-md border border-amber-300 bg-amber-50 p-4">
      <header className="mb-3 flex items-start gap-2">
        <ShieldAlert
          className="mt-0.5 size-5 shrink-0 text-amber-700"
          aria-hidden="true"
        />
        <div>
          <h2 className="text-lg font-semibold text-amber-900">Data Access</h2>
          <p className="text-sm text-amber-800">
            These toggles control whether roles can see sensitive teen data. Changes
            apply to every user assigned to this role and are audit-logged.
          </p>
        </div>
      </header>
      <div className="space-y-3">
        {dataAccessPerms.map((perm) => {
          const resource = perm.resource as DataAccessResource;
          const copy = COPY[resource];
          const id = `data-access-${perm.id}`;
          return (
            <div key={perm.id} className="flex items-start gap-3">
              <Checkbox
                id={id}
                checked={selectedIds.has(perm.id)}
                onCheckedChange={() => onToggle(perm.id)}
                disabled={disabled}
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor={id} className="font-medium text-amber-900">
                  {copy.title}
                </Label>
                <p className="text-xs text-amber-800">{copy.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
